import { Controller } from '@hotwired/stimulus'

// Fires the Facebook "InitiateCheckout" pixel on the visitor's FIRST genuine interaction
// with the order form — never on mere render.
//
// Order forms are frequently preloaded in a hidden popup iframe, and a hidden iframe still
// loads its src. Firing InitiateCheckout on render therefore counted a "started checkout"
// for every page view, with no real intent. Gating on interaction makes the event mean
// "actually started checkout" no matter how the form was loaded.
//
// The endpoint fires BOTH the server-side Conversions API event and returns a
// `fire_facebook_pixel` instruction (handled by AjaxHelper) for the browser pixel, sharing
// one eventID so Meta dedupes the two.
//
// Only genuine user gestures — NOT `focus` or `scroll`. Autofocus, lazy/gradual loading, and
// layout shifts can emit `scroll` (and the browser scrolling to an autofocused field fires it
// too), which would fire the pixel with no real intent. `pointerdown`/`touchstart` already
// cover tapping and mobile scrolling (a scroll starts with a touch), so we don't need `scroll`.
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart']

// Module-level so InitiateCheckout fires at most once per document — even if the order form
// (and therefore this controller) is rendered more than once on the page. A per-instance flag
// wouldn't help: each instance binds its own window listener, so one gesture would make every
// instance POST and produce duplicate events.
let fired = false

export default class extends Controller {
  static values = { url: String, token: String }

  connect() {
    this.onInteraction = this.onInteraction.bind(this)
    INTERACTION_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, this.onInteraction, { passive: true })
    )
  }

  disconnect() {
    this.removeListeners()
  }

  removeListeners() {
    INTERACTION_EVENTS.forEach((eventName) =>
      window.removeEventListener(eventName, this.onInteraction)
    )
  }

  onInteraction() {
    this.removeListeners()
    if (fired) return
    fired = true

    fetch(this.urlValue, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.tokenValue,
        Accept: 'application/json',
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data && window.AjaxHelper) window.AjaxHelper.doUpdate(data)
      })
      .catch(() => {
        // Best-effort tracking — ignore network errors
      })
  }
}
